import { IAAResponse } from '../config/config.types'
import { QueuedHostPageEvent } from '../host-page-events/host-page-event.types'
import { Params } from '../params/params.types'
import type { InlineGridTelemetry } from '../inline-grid/inline-grid-telemetry'

export const GA4_INIT_MSG_FROM_APP = 'GA4_INIT_MSG_FROM_APP'
export const ANALYTICS_EVENT_MSG_FROM_APP = 'ANALYTICS_EVENT_MSG_FROM_APP'
export const TEALIUM_ANALYTICS_EVENT_MSG_FROM_APP = 'TEALIUM_ANALYTICS_EVENT_MSG_FROM_APP'
export const DONATION_READY_MSG_FROM_APP = 'DONATION_READY_MSG_FROM_APP'
export const DONATION_CHANGED_MSG_FROM_APP = 'DONATION_CHANGED_MSG_FROM_APP'
export const DONATION_COMPLETED_MSG_FROM_APP = 'DONATION_COMPLETED_MSG_FROM_APP'
export const EMBEDDED_APP_VISIBLE = 'EMBEDDED_APP_VISIBLE' // Used to signify the modal is open and visible to the user
// Sent once the embedded app is ready to handle close requests itself
// (e.g. to show the abandoned-cart opt-in UI before the modal goes away).
export const DONATION_CLOSE_INTERCEPT_READY_MSG_FROM_APP =
  'DONATION_CLOSE_INTERCEPT_READY_MSG_FROM_APP'
// Sent when the embedded app has finished its close flow and the SDK should tear down the modal.
export const DONATION_CLOSE_CONFIRMED_MSG_FROM_APP = 'DONATION_CLOSE_CONFIRMED_MSG_FROM_APP'

export const SDK_INIT = 'SDK_INIT'
export const DONATION_PARAMS_UPDATE = 'DONATION_PARAMS_UPDATE'
export const HOST_PAGE_EVENTS_MSG_TO_APP = 'HOST_PAGE_EVENTS_MSG_TO_APP'
export const INLINE_GRID_TELEMETRY_MSG_TO_APP = 'INLINE_GRID_TELEMETRY_MSG_TO_APP'
// Sent instead of closing when the app has declared close-intercept readiness;
// the app answers later with DONATION_CLOSE_CONFIRMED_MSG_FROM_APP.
export const DONATION_CLOSE_REQUESTED_MSG_TO_APP = 'DONATION_CLOSE_REQUESTED_MSG_TO_APP'

export interface GA4InitMsgFromApp {
  type: typeof GA4_INIT_MSG_FROM_APP
  payload: {
    clientId: string
  }
}

export interface AnalyticsEventMsgFromApp {
  type: typeof ANALYTICS_EVENT_MSG_FROM_APP
  payload: {
    event: string
    data: {
      [key: string]: unknown
    }
  }
}

export interface TealiumAnalyticsEventMsgFromApp {
  type: typeof TEALIUM_ANALYTICS_EVENT_MSG_FROM_APP
  payload: {
    config: {
      account: string
      campaignId: string
      environment: string
      hasCartEnabled?: boolean | string
      orgId: string
      profile: string
    }
    data: {
      [key: string]: unknown
    }
    event: string
    eventType: 'page' | 'track'
  }
}

export interface DonationReadyMsgFromApp {
  type: typeof DONATION_READY_MSG_FROM_APP
  payload: unknown
}

export interface DonationChangedMsgFromApp {
  type: typeof DONATION_CHANGED_MSG_FROM_APP
  payload: {
    campaignId: string
    params: Params
  }
}

export interface DonationCompletedMsgFromApp {
  type: typeof DONATION_COMPLETED_MSG_FROM_APP
  payload: unknown
}

export interface DonationCloseInterceptReadyMsgFromApp {
  type: typeof DONATION_CLOSE_INTERCEPT_READY_MSG_FROM_APP
  payload: unknown
}

export interface DonationCloseConfirmedMsgFromApp {
  type: typeof DONATION_CLOSE_CONFIRMED_MSG_FROM_APP
  payload: unknown
}

export interface InitMsgToApp {
  type: typeof SDK_INIT
  payload: {
    safeguardToken: string
    iaa: IAAResponse
    /** Exact NPO page URL hosting the SDK, used for abandoned-cart return links. */
    hostUrl?: string
  }
}

/**
 * Used to signify to the application that it is visible to the user.
 */
export interface AppVisibleMessageToApp {
  type: typeof EMBEDDED_APP_VISIBLE
  payload: Record<string, never>
}

export type MsgType = (
  | GA4InitMsgFromApp
  | AnalyticsEventMsgFromApp
  | TealiumAnalyticsEventMsgFromApp
  | DonationReadyMsgFromApp
  | DonationChangedMsgFromApp
  | DonationCompletedMsgFromApp
  | DonationCloseInterceptReadyMsgFromApp
  | DonationCloseConfirmedMsgFromApp
  | InitMsgToApp
) & {
  source?: MessageEventSource | null
}

/**
 * Sent to the iframe when donation params (amount, frequency, etc.) change
 * after the iframe has already been loaded (e.g., preloaded iframes).
 */
export interface DonationParamsUpdateMsgToApp {
  type: typeof DONATION_PARAMS_UPDATE
  payload: {
    params: Params
    submit?: boolean
  }
}

export interface HostPageEventsMsgToApp {
  type: typeof HOST_PAGE_EVENTS_MSG_TO_APP
  payload: {
    campaignId: string
    events: QueuedHostPageEvent[]
  }
}

export interface DonationCloseRequestedMsgToApp {
  type: typeof DONATION_CLOSE_REQUESTED_MSG_TO_APP
  payload: Record<string, never>
}

export interface InlineGridTelemetryMsgToApp {
  type: typeof INLINE_GRID_TELEMETRY_MSG_TO_APP
  payload: InlineGridTelemetry
}

export type IframeMsgType =
  | InitMsgToApp
  | AppVisibleMessageToApp
  | DonationParamsUpdateMsgToApp
  | HostPageEventsMsgToApp
  | DonationCloseRequestedMsgToApp
  | InlineGridTelemetryMsgToApp
